import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/HomePage.css';
import {
  calculateCustomsFee,
  CalculationResult,
  findHSCode,
  searchHSCodes,
  getChapterNote,
  HsSearchResult,
} from '../utils/customsCalculator';
import tariffData from '../data/customsTariff.json';
import TariffImporter from '../components/TariffImporter';
import Seo from '../components/Seo';
import { topicPages, topicSlugs, topicImages } from '../data/topicPages';
import FlipCard, { FlipCardAccent } from '../components/FlipCard';
import HeroScene3D from '../components/HeroScene3D';
import ScrollReveal from '../components/ScrollReveal';
import AnimatedStat from '../components/AnimatedStat';
import ShipmentTrackerDemo from '../components/ShipmentTrackerDemo';
import { useLanguage } from '../contexts/LanguageContext';
import { portalTranslations } from '../i18n/portalTranslations';
import { translations } from '../i18n/translations';
import { servicePages, ServiceSlug } from '../data/servicePages';

const serviceAccents: FlipCardAccent[] = ['navy', 'gold', 'sky', 'teal'];

const serviceSlugs: ServiceSlug[] = [
  'customs-clearance',
  'documents',
  'shipment-tracking',
  'hs-classification',
];

const newsImages = [
  'https://images.unsplash.com/photo-1587385788270-742769b876c3?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1557426272-0e2955322841?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1593642632823-8f785ba67e45?w=600&h=400&fit=crop',
];

const partnersData = [
  { emoji: '⚓', name: 'الوكالات البحرية' },
  { emoji: '🏛️', name: 'وزارة الاقتصاد' },
  { emoji: '🛍️', name: 'وزارة التجارة الداخلية وحماية المستهلك' },
  { emoji: '🚪', name: 'هيئة المعابر والمنافذ السورية' },
  { emoji: '🚢', name: 'شركة مرفأ اللاذقية' },
];

const HomePage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const pt = portalTranslations[language];
  const location = useLocation();
  const showTariffImporter = false;

  const [importedJsonPreview, setImportedJsonPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    cargoType: '',
    weight: '',
    border: '',
    name: '',
    email: '',
    phone: ''
  });

  const [calculatorData, setCalculatorData] = useState({
    hsCode: '',
    border: '',
    weight: '',
    quantity: '',
    invoiceValue: '',
  });
  const [hsSearchQuery, setHsSearchQuery] = useState('');
  const [hsSuggestions, setHsSuggestions] = useState<HsSearchResult[]>([]);
  const [hsSearchOpen, setHsSearchOpen] = useState(false);
  const [selectedHsDescription, setSelectedHsDescription] = useState('');
  const [selectedChapterNote, setSelectedChapterNote] = useState('');

  const [calculatorResult, setCalculatorResult] = useState<CalculationResult | null>(null);
  const [resultPulse, setResultPulse] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    if (!location.hash) {
      return;
    }
    const timer = window.setTimeout(() => {
      const element = document.querySelector(location.hash);
      if (element) {
        const headerHeight = 70;
        const top = element.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuoteChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setQuoteForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCalculatorChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCalculatorData(prev => ({ ...prev, [name]: value }));
  };

  const handleHsSearchChange = (value: string) => {
    setHsSearchQuery(value);
    setCalculatorData(prev => ({ ...prev, hsCode: '' }));
    setSelectedHsDescription('');
    setSelectedChapterNote('');
    setCalculatorResult(null);

    const results = searchHSCodes(value, 12);
    setHsSuggestions(results);
    setHsSearchOpen(value.trim().length >= 2);
  };

  const selectHsSuggestion = (item: HsSearchResult) => {
    const code = item.code || '';
    setCalculatorData(prev => ({ ...prev, hsCode: code }));
    setHsSearchQuery(item.codeDotted || code);
    setSelectedHsDescription(item.description || '');
    setSelectedChapterNote(item.chapterNote || getChapterNote(code) || '');
    setHsSuggestions([]);
    setHsSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const form = e.target as HTMLFormElement;
      const formDataToSend = new FormData(form);
      
      // إرسال النموذج إلى Netlify Forms
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        alert(t.contact.successMessage);
        setFormData({
          name: '',
          email: '',
          phone: '',
          serviceType: '',
          message: ''
        });
      } else {
        alert('حدث خطأ في إرسال الرسالة. يرجى المحاولة لاحقاً.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('حدث خطأ في إرسال الرسالة. يرجى المحاولة لاحقاً.');
    }
  };

  const handleQuoteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Quote request:', quoteForm);
    alert(t.quickQuote.successMessage);
    setQuoteForm({
      cargoType: '',
      weight: '',
      border: '',
      name: '',
      email: '',
      phone: ''
    });
  };

  const calculateCustoms = () => {
    const weight = parseFloat(calculatorData.weight);
    const quantity = parseFloat(calculatorData.quantity);
    const invoiceValue = parseFloat(calculatorData.invoiceValue);
    const hsLookup = calculatorData.hsCode.trim() || hsSearchQuery.trim();

    if (!hsLookup) {
      alert(t.calculator.alerts.missingHsCode);
      return;
    }

    if (!invoiceValue || invoiceValue <= 0) {
      alert(t.calculator.alerts.invalidInvoice);
      return;
    }

    const hsCodeInfo = findHSCode(hsLookup);
    if (!hsCodeInfo) {
      alert(t.calculator.alerts.hsCodeNotFound(hsLookup));
      return;
    }

    const resolvedCode = hsCodeInfo.code || hsLookup;
    if (resolvedCode !== calculatorData.hsCode) {
      setCalculatorData(prev => ({ ...prev, hsCode: resolvedCode }));
      setHsSearchQuery(hsCodeInfo.codeDotted || resolvedCode);
      setSelectedHsDescription(hsCodeInfo.description || '');
      setSelectedChapterNote(getChapterNote(resolvedCode) || '');
    }

    const unit = hsCodeInfo.unit || 'weight';
    if (unit === 'count' && (!quantity || quantity <= 0)) {
      alert(t.calculator.alerts.invalidQuantity);
      return;
    }
    if (unit !== 'count' && (!weight || weight <= 0)) {
      alert(t.calculator.alerts.invalidWeight);
      return;
    }

    const result = calculateCustomsFee({
      weight: weight || 0,
      quantity: quantity || 0,
      hsCode: resolvedCode,
      border: calculatorData.border || 'land',
      invoiceValue,
    });

    if (result) {
      setCalculatorResult(result);
      setResultPulse(true);
      window.setTimeout(() => setResultPulse(false), 900);
    } else {
      alert(t.calculator.alerts.error);
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="home-page">
      <Seo
        title="شركات التخليص الجمركي في سوريا | الشراع — اللاذقية"
        description="شركة الشراع للتخليص الجمركي في اللاذقية، سوريا. سجل تجاري 10272. تخليص بحري وبري، حاويات ومبرّدات عبر مرفأ اللاذقية ومعبر نصيب. الاستشارات ودراسة الأسعار خدمة مدفوعة."
        path="/"
        lang={language === 'de' ? 'de' : language === 'en' ? 'en' : 'ar'}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'شركات التخليص الجمركي في سوريا | الشراع — اللاذقية',
          description:
            'شركة الشراع للتخليص الجمركي في اللاذقية، سوريا، عبر الموانئ والمعابر السورية.',
          url: 'https://alshiraa-customs.com/',
          inLanguage: language,
          isPartOf: { '@id': 'https://alshiraa-customs.com/#website' },
          about: {
            '@type': 'ProfessionalService',
            name: 'شركة الشراع للتخليص الجمركي',
            telephone: '+963933612861',
          },
        }}
      />
      {/* Hero Section */}
      <section id="home" className="hero hero-with-3d">
        <HeroScene3D />
        <div className="hero-waves" aria-hidden="true">
          <div className="hero-wave-layer hero-wave-a" />
          <div className="hero-wave-layer hero-wave-b" />
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">{t.hero.title}</h1>
            <p className="hero-tagline">{t.hero.tagline}</p>
            <div className="hero-stats">
              {t.hero.stats.map((stat, index) => (
                <AnimatedStat
                  key={index}
                  value={stat.value}
                  label={stat.label}
                  className="hero-stat-item"
                  numberClassName="hero-stat-number"
                  labelClassName="hero-stat-label"
                  showBar
                />
              ))}
            </div>
            <div className="hero-buttons">
              <a href="#services" className="btn btn-primary btn-shimmer is-shimmering">
                {t.hero.primaryCta}
              </a>
              <a href="#contact" className="btn btn-secondary btn-shimmer is-shimmering">
                {t.hero.secondaryCta}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <ScrollReveal variant="fade-up">
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-logo-section">
            <img
              src={encodeURI('/شعار صفحة .png')}
              alt="شركة الشراع للتخليص الجمركي في اللاذقية، سوريا"
              className="about-logo-image"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
          <h2 className="section-title">{t.about.title}</h2>
          <div className="about-content">
            <div className="about-content-wrapper">
              <div className="about-text">
                <p>{t.about.description}</p>
              </div>
              <div className="about-images about-images-3d">
                <div className="transport-image transport-tilt">
                  <img src="https://images.unsplash.com/photo-1605745341112-85968b19335b?w=600&h=400&fit=crop" alt="Sea freight" loading="lazy" />
                  <span>{t.about.transportLabels.sea}</span>
                </div>
                <div className="transport-image transport-tilt">
                  <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&h=400&fit=crop" alt="Air freight" loading="lazy" />
                  <span>{t.about.transportLabels.air}</span>
                </div>
                <div className="transport-image transport-tilt">
                  <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop" alt="Road freight" loading="lazy" />
                  <span>{t.about.transportLabels.land}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Regional clients */}
      <ScrollReveal delay={80} variant="fade-up" asStagger>
      <section id="regions" className="regions-section">
        <div className="container">
          <h2 className="section-title">{t.regions.title}</h2>
          <p className="section-subtitle">{t.regions.subtitle}</p>
          <p className="regions-intro">{t.regions.description}</p>
          <div className="regions-grid">
            {t.regions.countries.map((country, index) => (
              <Link
                className="region-card interactive-lift stagger-item"
                key={country.slug}
                to={`/${country.slug}`}
                style={{ transitionDelay: `${index * 70}ms` }}
              >
                <h3>{country.name}</h3>
                <p>{country.detail}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Services */}
      <ScrollReveal delay={120} variant="fade-scale" asStagger>
      <section id="services" className="services-section">
        <div className="container">
          <h2 className="section-title">{t.services.title}</h2>
          <div className="services-grid">
            {t.services.cards.map((card, index) => {
              const slug = serviceSlugs[index];
              const serviceCopy = slug ? servicePages[slug][language] : null;
              return (
                <div
                  key={slug || index}
                  className="stagger-item"
                  style={{ transitionDelay: `${index * 90}ms` }}
                >
                  <FlipCard
                    icon={card.icon}
                    title={card.title}
                    frontText={card.description}
                    backText={serviceCopy?.intro || card.description}
                    backLink={`/services/${slug}`}
                    backLinkLabel={t.news.readMore}
                    hint={pt.flipHint}
                    accent={serviceAccents[index % serviceAccents.length]}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>
      </ScrollReveal>

      <ScrollReveal delay={100} variant="fade-up" asStagger>
      <section id="seo-topics" className="regions-section">
        <div className="container">
          <h2 className="section-title">
            {language === 'ar'
              ? 'تخليص جمركي وشحن دولي'
              : language === 'en'
                ? 'Customs clearance & international shipping'
                : 'Zoll & internationaler Versand'}
          </h2>
          <p className="section-subtitle">
            {language === 'ar'
              ? 'شركات التخليص في سوريا، تخليص اللاذقية، شحن أمريكا والصين، FCL وLCL، والشحن الجوي.'
              : language === 'en'
                ? 'Latakia clearance, USA & China lanes, FCL, LCL and air freight guides.'
                : 'Zoll Latakia, USA & China, FCL, LCL und Luftfracht.'}
          </p>
          <div className="regions-grid">
            {topicSlugs.map((slug, index) => {
              const topic = topicPages[slug][language];
              const image = topicImages[slug];
              return (
                <Link
                  className="region-card interactive-lift stagger-item"
                  key={slug}
                  to={`/${slug}`}
                  style={{ transitionDelay: `${index * 55}ms` }}
                >
                  {image ? (
                    <img
                      className="region-card-image"
                      src={image.src}
                      alt={image.alt[language]}
                      width={600}
                      height={140}
                      loading="lazy"
                    />
                  ) : null}
                  <h3>{topic.name}</h3>
                  <p>{topic.intro}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      </ScrollReveal>

      <ShipmentTrackerDemo />

      {/* Quick Quote */}
      <ScrollReveal variant="fade-up">
      <section id="quick-quote" className="quick-quote-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.quickQuote.title}</h2>
          <p className="section-subtitle quote-subtitle">{t.quickQuote.subtitle}</p>
          <form className="quote-form quote-form-glow" onSubmit={handleQuoteSubmit}>
            <div className="quote-form-grid">
              <div className="form-group">
                <label htmlFor="quote-cargoType">{t.quickQuote.fields.cargoType}</label>
                <select
                  id="quote-cargoType"
                  name="cargoType"
                  value={quoteForm.cargoType}
                  onChange={handleQuoteChange}
                  required
                >
                  <option value="">{t.quickQuote.fields.cargoPlaceholder}</option>
                  {t.quickQuote.cargoOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="quote-weight">{t.quickQuote.fields.weight}</label>
                <input
                  type="number"
                  id="quote-weight"
                  name="weight"
                  value={quoteForm.weight}
                  onChange={handleQuoteChange}
                  placeholder={t.quickQuote.fields.weightPlaceholder}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-border">{t.quickQuote.fields.border}</label>
                <select
                  id="quote-border"
                  name="border"
                  value={quoteForm.border}
                  onChange={handleQuoteChange}
                  required
                >
                  <option value="">{t.quickQuote.fields.borderPlaceholder}</option>
                  {t.quickQuote.borderOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="quote-name">{t.quickQuote.fields.name}</label>
                <input
                  type="text"
                  id="quote-name"
                  name="name"
                  value={quoteForm.name}
                  onChange={handleQuoteChange}
                  placeholder={t.quickQuote.fields.namePlaceholder}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-email">{t.quickQuote.fields.email}</label>
                <input
                  type="email"
                  id="quote-email"
                  name="email"
                  value={quoteForm.email}
                  onChange={handleQuoteChange}
                  placeholder={t.quickQuote.fields.emailPlaceholder}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-phone">{t.quickQuote.fields.phone}</label>
                <input
                  type="tel"
                  id="quote-phone"
                  name="phone"
                  value={quoteForm.phone}
                  onChange={handleQuoteChange}
                  placeholder={t.quickQuote.fields.phonePlaceholder}
                  required
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-shimmer is-shimmering">
              {t.quickQuote.button}
            </button>
          </form>
        </div>
      </section>
      </ScrollReveal>

      {/* Calculator */}
      <ScrollReveal variant="fade-up">
      <section id="calculator" className="calculator-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.calculator.title}</h2>
          <p className="section-subtitle">{t.calculator.subtitle}</p>

          {showTariffImporter && (
            <div className="tariff-importer-section">
              <details className="importer-details">
                <summary className="importer-summary">🔧 تحديث/استيراد التعرفة الجمركية من PDF</summary>
                <div className="importer-content">
                  {importedJsonPreview && (
                    <div className="json-preview-section" style={{
                      marginBottom: '2rem',
                      padding: '1.5rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '2px solid #0077B6'
                    }}>
                      <h4 style={{ color: '#003366', marginBottom: '1rem' }}>📄 معاينة ملف JSON المحدث:</h4>
                      <div style={{
                        backgroundColor: '#fff3cd',
                        padding: '1rem',
                        borderRadius: '4px',
                        marginBottom: '1rem',
                        border: '1px solid #ffc107'
                      }}>
                        <strong>⚠️ خطوات مهمة:</strong>
                        <ol style={{ marginTop: '0.5rem', paddingRight: '1.5rem' }}>
                          <li>تم تحميل الملف تلقائياً باسم <code>customsTariff-updated.json</code></li>
                          <li>انسخ المحتوى من المعاينة أدناه (أو من الملف المحمّل)</li>
                          <li>افتح ملف <code>src/data/customsTariff.json</code></li>
                          <li>استبدل كامل المحتوى بالمحتوى الجديد</li>
                          <li>احفظ الملف - ستتحدث الآلة الحاسبة تلقائياً!</li>
                        </ol>
                      </div>
                      <details>
                        <summary style={{ cursor: 'pointer', color: '#0077B6', fontWeight: 'bold', marginBottom: '1rem' }}>
                          انقر لعرض/إخفاء محتوى JSON الكامل
                        </summary>
                        <pre style={{
                          backgroundColor: '#fff',
                          padding: '1rem',
                          borderRadius: '4px',
                          overflow: 'auto',
                          maxHeight: '400px',
                          fontSize: '0.9rem',
                          direction: 'ltr',
                          textAlign: 'left'
                        }}>
                          {importedJsonPreview}
                        </pre>
                      </details>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(importedJsonPreview);
                          alert('✅ تم نسخ محتوى JSON إلى الحافظة!');
                        }}
                        className="btn btn-secondary"
                        style={{ marginTop: '1rem' }}
                      >
                        📋 نسخ JSON إلى الحافظة
                      </button>
                      <button
                        onClick={() => setImportedJsonPreview(null)}
                        className="btn btn-secondary"
                        style={{ marginTop: '1rem', marginRight: '0.5rem' }}
                      >
                        ✖️ إخفاء المعاينة
                      </button>
                    </div>
                  )}
                  <TariffImporter
                    onImport={(data) => {
                      const existingData = JSON.parse(localStorage.getItem('importedTariffData') || '[]');
                      const mergedData = [...existingData, ...data];
                      localStorage.setItem('importedTariffData', JSON.stringify(mergedData));

                      const jsonData = {
                        currency: 'USD',
                        currencySymbol: '$',
                        hsCodes: {},
                        borderFees: (tariffData as any).borderFees || {},
                        note: 'تم استيراد هذه البيانات من ملف PDF'
                      };

                      const existingHsCodes = (tariffData as any).hsCodes || {};
                      Object.assign(jsonData.hsCodes, existingHsCodes);

                      let updatedCount = 0;
                      let newCount = 0;
                      data.forEach(item => {
                        const isUpdate = existingHsCodes[item.code] ? true : false;
                        (jsonData.hsCodes as any)[item.code] = {
                          code: item.code,
                          description: item.description || (existingHsCodes[item.code]?.description || 'لا يوجد وصف'),
                          ratePerTon: item.ratePerTon,
                          category: item.category || existingHsCodes[item.code]?.category || 'general'
                        };
                        if (isUpdate) updatedCount++;
                        else newCount++;
                      });

                      const jsonString = JSON.stringify(jsonData, null, 2);
                      const blob = new Blob([jsonString], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'customsTariff-updated.json';
                      a.click();
                      URL.revokeObjectURL(url);

                      const totalCodes = Object.keys(jsonData.hsCodes).length;
                      const sampleCodes = Object.keys(jsonData.hsCodes).slice(0, 10);
                      const sampleText = sampleCodes.map(code => {
                        const item = (jsonData.hsCodes as any)[code];
                        return `  ${code}: ${item.description.substring(0, 40)}... - $${item.ratePerTon}/ton`;
                      }).join('\n');

                      const summary = `✅ تم استيراد ${data.length} بند جمركي بنجاح!\n\n` +
                        `📊 إجمالي البنود في الملف: ${totalCodes}\n\n` +
                        `📋 عينة من البنود المستوردة:\n${sampleText}${totalCodes > 10 ? `\n  ... و ${totalCodes - 10} بند آخر` : ''}\n\n` +
                        `📥 تم تحميل ملف: customsTariff-updated.json تلقائياً\n\n` +
                        `⚠️ الخطوات المطلوبة:\n` +
                        `1. ✅ تم تحميل الملف تلقائياً (تحقق من مجلد التحميلات)\n` +
                        `2. افتح ملف customsTariff-updated.json من مجلد التحميلات\n` +
                        `3. انسخ كامل المحتوى (Ctrl+A ثم Ctrl+C)\n` +
                        `4. افتح ملف src/data/customsTariff.json في محرر النصوص\n` +
                        `5. استبدل كامل المحتوى بالمحتوى الجديد (Ctrl+A ثم Ctrl+V)\n` +
                        `6. احفظ الملف (Ctrl+S)\n` +
                        `7. ✅ ستتحدث الآلة الحاسبة تلقائياً بجميع البنود الجديدة!\n\n` +
                        `💡 ملاحظة: يمكنك أيضاً نسخ JSON من المعاينة في الصفحة بدلاً من فتح الملف`;

                      setImportedJsonPreview(jsonString);
                      alert(summary);
                      console.log('📄 ملف JSON المحدث:', jsonData);
                      console.log(`📊 إجمالي البنود: ${totalCodes}`);
                      console.log('📋 محتوى JSON الكامل:', jsonString);
                    }}
                  />
                  <div className="import-instructions">
                    <h4>تعليمات الاستيراد:</h4>
                    <ol>
                      <li><strong>من PDF:</strong> افتح ملف PDF وانسخ الجداول صفحة بصفحة</li>
                      <li><strong>الصيغة المطلوبة:</strong> البند - الوصف - السعر (USD/ton)</li>
                      <li><strong>مثال:</strong> 8517 - أجهزة هاتف - 150</li>
                      <li>يمكنك نسخ عدة صفحات دفعة واحدة</li>
                      <li>أو قم بتحويل PDF إلى Excel/CSV أولاً ثم استورد الملف</li>
                    </ol>
                  </div>
                </div>
              </details>
            </div>
          )}

          <div className="calculator-container">
            <div className="calculator-form">
              <div className="form-group hs-search-group">
                <label htmlFor="calc-hsCode">{t.calculator.fields.hsCode}</label>
                <div className="hs-search-box">
                  <input
                    type="text"
                    id="calc-hsCode"
                    name="hsSearch"
                    value={hsSearchQuery}
                    onChange={(e) => handleHsSearchChange(e.target.value)}
                    onFocus={() => {
                      if (hsSearchQuery.trim().length >= 2) {
                        setHsSuggestions(searchHSCodes(hsSearchQuery, 12));
                        setHsSearchOpen(true);
                      }
                    }}
                    onBlur={() => {
                      window.setTimeout(() => setHsSearchOpen(false), 150);
                    }}
                    placeholder={t.calculator.fields.hsCodePlaceholder}
                    autoComplete="off"
                    required
                  />
                  {hsSearchOpen && (
                    <div className="hs-suggestions" role="listbox">
                      {hsSuggestions.length === 0 ? (
                        <div className="hs-suggestion-empty">{t.calculator.fields.noResults}</div>
                      ) : (
                        hsSuggestions.map((item) => (
                          <button
                            key={item.code}
                            type="button"
                            className="hs-suggestion-item"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectHsSuggestion(item)}
                          >
                            <span className="hs-suggestion-code">{item.codeDotted || item.code}</span>
                            <span className="hs-suggestion-desc">{item.description}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <p className="hs-search-hint">{t.calculator.fields.hsSearchHint}</p>
                {selectedHsDescription && (
                  <p className="hs-selected-desc">{selectedHsDescription}</p>
                )}
                {selectedChapterNote && (
                  <p className="hs-chapter-note">
                    <strong>{t.calculator.fields.chapterNote}:</strong> {selectedChapterNote}
                  </p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="calc-invoice">{t.calculator.fields.invoiceValue}</label>
                <input
                  type="number"
                  id="calc-invoice"
                  name="invoiceValue"
                  value={calculatorData.invoiceValue}
                  onChange={handleCalculatorChange}
                  placeholder={t.calculator.fields.invoiceValuePlaceholder}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="calc-weight">{t.calculator.fields.weight}</label>
                <input
                  type="number"
                  id="calc-weight"
                  name="weight"
                  value={calculatorData.weight}
                  onChange={handleCalculatorChange}
                  placeholder={t.calculator.fields.weightPlaceholder}
                  min="0"
                  step="0.001"
                />
              </div>
              <div className="form-group">
                <label htmlFor="calc-quantity">{t.calculator.fields.quantity}</label>
                <input
                  type="number"
                  id="calc-quantity"
                  name="quantity"
                  value={calculatorData.quantity}
                  onChange={handleCalculatorChange}
                  placeholder={t.calculator.fields.quantityPlaceholder}
                  min="0"
                  step="1"
                />
              </div>
              <div className="form-group">
                <label htmlFor="calc-border">{t.calculator.fields.border}</label>
                <select
                  id="calc-border"
                  name="border"
                  value={calculatorData.border}
                  onChange={handleCalculatorChange}
                >
                  <option value="">{t.calculator.fields.borderPlaceholder}</option>
                  {t.quickQuote.borderOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <button type="button" onClick={calculateCustoms} className="btn btn-primary">{t.calculator.button}</button>
            </div>
            {calculatorResult !== null && (
              <div className="calculator-result">
                <h3>{t.calculator.result.title}</h3>
                <div className={`result-amount ${resultPulse ? 'is-pulsing' : ''}`}>
                  {calculatorResult.currencySymbol}{calculatorResult.total.toFixed(2)}
                  <span className="currency-label"> {calculatorResult.currency}</span>
                </div>
                <div className="result-breakdown">
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.hsCode}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.hsCode}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.description}</span>
                    <span className="breakdown-value">
                      {calculatorResult.breakdown.hsCodeDescription || calculatorResult.breakdown.cargoTypeDescription}
                    </span>
                  </div>
                  {calculatorResult.breakdown.chapterNote && (
                    <div className="breakdown-item">
                      <span className="breakdown-label">{t.calculator.result.chapterNote}</span>
                      <span className="breakdown-value">{calculatorResult.breakdown.chapterNote}</span>
                    </div>
                  )}
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.unit}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.unitLabel}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.invoiceValue}</span>
                    <span className="breakdown-value">{calculatorResult.currencySymbol}{calculatorResult.breakdown.invoiceValue.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.weight}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.weight || 0} طن</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.quantity}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.quantity}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.rateImport}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.importDutyRate}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.rateService}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.serviceFeeRate}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.advancePercent}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.advancePercent}%</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.consumptionPercent}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.consumptionPercent}%</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.border}</span>
                    <span className="breakdown-value">{calculatorResult.breakdown.borderDescription}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.importDuty}</span>
                    <span className="breakdown-value">{calculatorResult.currencySymbol}{calculatorResult.importDutyAmount.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.serviceFee}</span>
                    <span className="breakdown-value">{calculatorResult.currencySymbol}{calculatorResult.serviceFeeAmount.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.tariffTotal}</span>
                    <span className="breakdown-value">{calculatorResult.currencySymbol}{calculatorResult.tariffTotalAmount.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.borderFee}</span>
                    <span className="breakdown-value">{calculatorResult.currencySymbol}{calculatorResult.borderFee.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.declarationAdvance}</span>
                    <span className="breakdown-value">{calculatorResult.currencySymbol}{calculatorResult.declarationAdvance.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.calculator.result.consumptionTax}</span>
                    <span className="breakdown-value">{calculatorResult.currencySymbol}{calculatorResult.consumptionTax.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item breakdown-total" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid var(--border-light)' }}>
                    <span className="breakdown-label" style={{ fontWeight: 'bold' }}>{t.calculator.result.grandTotal}</span>
                    <span className="breakdown-value" style={{ fontWeight: 'bold', color: 'var(--primary-dark-blue)' }}>
                      {calculatorResult.currencySymbol}{calculatorResult.total.toFixed(2)}
                    </span>
                  </div>
                </div>
                <p className="result-note">{t.calculator.result.note}</p>
              </div>
            )}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Documents */}
      <ScrollReveal variant="fade-up" asStagger>
      <section id="documents" className="documents-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.documents.title}</h2>
          <p className="section-subtitle">{t.documents.subtitle}</p>
          <div className="documents-grid">
            {t.documents.categories.map((category, index) => (
              <div
                className="document-card stagger-item"
                key={index}
                style={{ transitionDelay: `${index * 60}ms` }}
              >
                <div className="document-icon">{category.icon}</div>
                <h3>{category.title}</h3>
                <ul>
                  {category.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Borders */}
      <ScrollReveal variant="fade-up" asStagger>
      <section id="borders" className="borders-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.borders.title}</h2>
          <p className="section-subtitle">{t.borders.subtitle}</p>
          <div className="borders-map-container">
            <div className="syria-map">
              {t.borders.mapPoints.map((point, index) => (
                <div
                  key={index}
                  className="map-point"
                  style={{ top: point.top, right: point.right, animationDelay: `${index * 0.35}s` }}
                  title={point.label}
                >
                  <span className="map-marker">📍</span>
                  <div className="map-tooltip">{point.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="borders-grid">
            <div className="border-card stagger-item" style={{ transitionDelay: '0ms' }}>
              <div className="border-card-image">
                <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=300&fit=crop" alt="Land crossings" loading="lazy" />
              </div>
              <h3>{t.borders.landTitle}</h3>
              <ul>
                {t.borders.landList.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="border-card stagger-item" style={{ transitionDelay: '80ms' }}>
              <div className="border-card-image">
                <img src="https://images.unsplash.com/photo-1605745341112-85968b19335b?w=600&h=300&fit=crop" alt="Sea crossings" loading="lazy" />
              </div>
              <h3>{t.borders.seaTitle}</h3>
              <ul>
                {t.borders.seaList.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="border-card stagger-item" style={{ transitionDelay: '160ms' }}>
              <div className="border-card-image">
                <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&h=300&fit=crop" alt="Air crossings" loading="lazy" />
              </div>
              <h3>{t.borders.airTitle}</h3>
              <ul>
                {t.borders.airList.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Testimonials */}
      <ScrollReveal variant="fade-scale" asStagger>
      <section id="testimonials" className="testimonials-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.testimonials.title}</h2>
          <p className="section-subtitle">{t.testimonials.subtitle}</p>
          <div className="testimonials-grid">
            {t.testimonials.cards.map((card, index) => (
              <div
                className="testimonial-card stagger-item"
                key={index}
                style={{ transitionDelay: `${index * 70}ms` }}
              >
                <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
                <p className="testimonial-text">"{card.quote}"</p>
                <div className="testimonial-author">
                  <strong>{card.author}</strong>
                  <span>{card.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Certifications */}
      <ScrollReveal variant="fade-up" asStagger>
      <section id="certifications" className="certifications-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.certifications.title}</h2>
          <p className="section-subtitle">{t.certifications.subtitle}</p>
          <div className="certifications-grid">
            {t.certifications.cards.map((card, index) => (
              <div
                className="certification-card stagger-item"
                key={index}
                style={{ transitionDelay: `${index * 60}ms` }}
              >
                <div className="certification-icon">{card.icon}</div>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Team */}
      <ScrollReveal variant="fade-scale" asStagger>
      <section id="team" className="team-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.team.title}</h2>
          <div className="team-grid">
            {t.team.members.map((member, index) => (
              <div
                className="team-member stagger-item"
                key={`${member.name}-${index}`}
                style={{ transitionDelay: `${index * 60}ms` }}
              >
                <div className="member-avatar">{member.icon}</div>
                <h3>{member.name}</h3>
                <p className="member-role">{member.role}</p>
                <p className="member-bio">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Partners */}
      <ScrollReveal variant="fade-up" asStagger>
      <section id="partners" className="partners-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.partners.title}</h2>
          <p className="section-subtitle">{t.partners.subtitle}</p>
          <div className="partners-grid">
            {partnersData.map((partner, index) => (
              <div
                className="partner-logo stagger-item"
                key={index}
                style={{ transitionDelay: `${index * 70}ms` }}
              >
                <div className="partner-emoji">{partner.emoji}</div>
                <p>{partner.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* News */}
      <ScrollReveal variant="fade-up" asStagger>
      <section id="news" className="news-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.news.title}</h2>
          <div className="news-grid">
            {t.news.cards.map((card, index) => (
              <div
                className="news-card stagger-item"
                key={index}
                style={{ transitionDelay: `${index * 70}ms` }}
              >
                <img src={newsImages[index % newsImages.length]} alt={card.title} loading="lazy" />
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                <a href="#" className="btn btn-secondary">{t.news.readMore}</a>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* FAQ */}
      <ScrollReveal variant="fade-up" asStagger>
      <section id="faq" className="faq-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.faq.title}</h2>
          <p className="section-subtitle">{t.faq.subtitle}</p>
          <div className="faq-grid">
            {t.faq.items.map((item, index) => (
              <div
                className={`faq-item stagger-item ${openFaq === index ? 'open' : ''}`}
                key={index}
                style={{ transitionDelay: `${index * 45}ms` }}
              >
                <div className="faq-question" onClick={() => toggleFaq(index)}>
                  <h4>{item.question}</h4>
                  <span className="faq-icon">{openFaq === index ? '▲' : '▼'}</span>
                </div>
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Contact */}
      <ScrollReveal variant="fade-up">
      <section id="contact" className="contact-section section-shell">
        <div className="container">
          <h2 className="section-title">{t.contact.title}</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>{t.contact.infoTitle}</h3>
              {t.contact.infoItems.map((item, index) => {
                // إصلاح اتجاه علامة + للأرقام في العربية
                const text = item.text;
                const parts = text.split(/(\+963[\d\s\|]+)/);
                return (
                  <div className="contact-item" key={index}>
                    <span className="contact-icon">{item.icon}</span>
                    <span>
                      {parts.map((part, idx) => 
                        part.startsWith('+963') ? (
                          <span key={idx} dir="ltr" style={{ display: 'inline-block', unicodeBidi: 'embed' }}>{part}</span>
                        ) : (
                          <span key={idx}>{part}</span>
                        )
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            <form 
              className="contact-form" 
              onSubmit={handleSubmit}
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="contact" />
              <input type="hidden" name="bot-field" />
              <div className="form-group">
                <label htmlFor="name">{t.contact.fields.name}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t.contact.fields.namePlaceholder}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">{t.contact.fields.email}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t.contact.fields.emailPlaceholder}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">{t.contact.fields.phone}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t.contact.fields.phonePlaceholder}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="serviceType">{t.contact.fields.serviceType}</label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t.contact.fields.servicePlaceholder}</option>
                  {t.contact.serviceOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="message">{t.contact.fields.message}</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  required
                ></textarea>
              </div>
              <button type="submit" className="btn btn-primary btn-shimmer is-shimmering">
                {t.contact.button}
              </button>
            </form>
          </div>
        </div>
      </section>
      </ScrollReveal>
    </div>
  );
};

export default HomePage;


